import { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import "../styles/createUser.css";

// Helper: Generate Employee ID
const generateEmployeeId = () => {
  const year = new Date().getFullYear();
  const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
  return `rchcswf-${year}-${randomNum}`;
};

const initialState = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  role: "",
  stateId: "",
  districtId: "",
  blockId: "",
  gpId: "",
  phone: "",
  employeeId: generateEmployeeId(),
  level: "",
  village: "",
  postOffice: "",
  pin: "",
  aadhaar: "",
  pan: "",
  bankName: "",
  accountNumber: "",
  ifsc: "",
  branch: "",
};

export default function CreateUser() {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gps, setGps] = useState([]);
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  // Payment states
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [paymentCleared, setPaymentCleared] = useState(false);
  
  // File states
  const [files, setFiles] = useState({
    aadhaarFile: null,
    panFile: null,
    passbookFile: null,
    photoFile: null,
  });

  // Preview URLs
  const [previews, setPreviews] = useState({
    aadhaar: null,
    pan: null,
    passbook: null,
    photo: null,
  });

  // Refs to file inputs for clearing
  const aadhaarRef = useRef();
  const panRef = useRef();
  const passbookRef = useRef();
  const photoRef = useRef();

  useEffect(() => {
    loadStates();
  }, []);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const errors = [];
    let strength = 0;
    
    if (password.length >= 8) {
      strength++;
    } else {
      errors.push("At least 8 characters");
    }
    
    if (/[a-z]/.test(password)) {
      strength++;
    } else {
      errors.push("At least one lowercase letter");
    }
    
    if (/[A-Z]/.test(password)) {
      strength++;
    } else {
      errors.push("At least one uppercase letter");
    }
    
    if (/[0-9]/.test(password)) {
      strength++;
    } else {
      errors.push("At least one digit");
    }
    
    if (/[@#$%^&+=!]/.test(password)) {
      strength++;
    } else {
      errors.push("At least one special character (@#$%^&+=!)");
    }
    
    if (!/\s/.test(password)) {
      strength++;
    } else {
      errors.push("No spaces allowed");
    }
    
    setPasswordStrength(strength);
    setPasswordErrors(errors);
    return { strength, errors };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setForm({ ...form, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const loadStates = async () => {
    try {
      const res = await api.get("/states");
      setStates(res.data);
    } catch (err) {
      console.error("Error loading states", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleStateChange = async (e) => {
    const stateId = e.target.value;
    setForm({ ...form, stateId, districtId: "", blockId: "", gpId: "" });
    if (stateId) {
      const res = await api.get(`/districts?stateId=${stateId}`);
      setDistricts(res.data);
    } else {
      setDistricts([]);
    }
    setBlocks([]);
    setGps([]);
  };

  const handleDistrictChange = async (e) => {
    const districtId = e.target.value;
    setForm({ ...form, districtId, blockId: "", gpId: "" });
    if (districtId) {
      const res = await api.get(`/blocks?districtId=${districtId}`);
      setBlocks(res.data);
    } else {
      setBlocks([]);
    }
    setGps([]);
  };

  const handleBlockChange = async (e) => {
    const blockId = e.target.value;
    setForm({ ...form, blockId, gpId: "" });
    if (blockId) {
      const res = await api.get(`/gps?blockId=${blockId}`);
      setGps(res.data);
    } else {
      setGps([]);
    }
  };

  const handleFileChange = (type, file) => {
    let fileKey = "";
    let previewKey = "";
    
    if (type === "aadhaar") {
      fileKey = "aadhaarFile";
      previewKey = "aadhaar";
    } else if (type === "pan") {
      fileKey = "panFile";
      previewKey = "pan";
    } else if (type === "passbook") {
      fileKey = "passbookFile";
      previewKey = "passbook";
    } else if (type === "photo") {
      fileKey = "photoFile";
      previewKey = "photo";
    }
    
    setFiles(prev => ({ ...prev, [fileKey]: file }));
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [previewKey]: previewUrl }));
    } else {
      setPreviews(prev => ({ ...prev, [previewKey]: null }));
    }
  };

  const clearFileInputs = () => {
    Object.values(previews).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    setPreviews({
      aadhaar: null,
      pan: null,
      passbook: null,
      photo: null,
    });
    setFiles({
      aadhaarFile: null,
      panFile: null,
      passbookFile: null,
      photoFile: null,
    });
    if (aadhaarRef.current) aadhaarRef.current.value = "";
    if (panRef.current) panRef.current.value = "";
    if (passbookRef.current) passbookRef.current.value = "";
    if (photoRef.current) photoRef.current.value = "";
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all fields?")) {
      setForm({ ...initialState, employeeId: generateEmployeeId() });
      setPaymentAmount("");
      setPaymentDescription("");
      setPaymentCleared(false);
      clearFileInputs();
      setPasswordStrength(0);
      setPasswordErrors([]);
    }
  };

const validateForm = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[6-9][0-9]{9}$/;
  const pinRegex = /^[1-9][0-9]{5}$/;
  const aadhaarRegex = /^[2-9][0-9]{11}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const accountRegex = /^[0-9]{9,18}$/;
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const passwordRegex =
    /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!])(?=\S+$).{8,}$/;

  if (!form.name.trim()) {
    alert("Full Name is required");
    return false;
  }

  if (!emailRegex.test(form.email.trim())) {
    alert("Enter valid email address");
    return false;
  }

  if (!passwordRegex.test(form.password)) {
    alert("Password must contain minimum 8 characters, uppercase, lowercase, number and special character");
    return false;
  }

  if (form.password !== form.confirmPassword) {
    alert("Passwords do not match");
    return false;
  }

  if (!form.role) {
    alert("Please select role");
    return false;
  }

  if (!phoneRegex.test(form.phone.trim())) {
    alert("Enter valid 10 digit Indian mobile number");
    return false;
  }

  if (!pinRegex.test(form.pin.trim())) {
    alert("Enter valid 6 digit PIN code");
    return false;
  }

  if (!aadhaarRegex.test(form.aadhaar.trim())) {
    alert("Enter valid 12 digit Aadhaar number");
    return false;
  }

  if (!panRegex.test(form.pan.trim().toUpperCase())) {
    alert("Enter valid PAN number like ABCDE1234F");
    return false;
  }

  if (!form.bankName.trim()) {
    alert("Bank name is required");
    return false;
  }

  if (!accountRegex.test(form.accountNumber.trim())) {
    alert("Enter valid bank account number, 9 to 18 digits");
    return false;
  }

  if (!ifscRegex.test(form.ifsc.trim().toUpperCase())) {
    alert("Enter valid IFSC code like SBIN0001234");
    return false;
  }

  if (!form.branch.trim()) {
    alert("Branch name is required");
    return false;
  }

  if (!files.photoFile) {
    alert("Photo is mandatory");
    return false;
  }

  return true;
};

 const save = async () => {
  // 1. Check required fields
  if (!validateForm()) return;
  if (!form.name || !form.email || !form.password || !form.role) {
    alert("Please fill in all required fields (Name, Email, Password, Role)");
    return;
  }

  // 2. Check if passwords match
  if (form.password !== form.confirmPassword) {
    alert("❌ Passwords do not match");
    return;
  }

  // 3. Validate password strength
  const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!])(?=\S+$).{8,}$/;
  if (!passwordRegex.test(form.password)) {
    alert("❌ Password must contain:\n- Minimum 8 characters\n- At least one digit\n- At least one lowercase letter\n- At least one uppercase letter\n- At least one special character (@#$%^&+=!)\n- No spaces");
    return;
  }
  if (paymentMethod === "CASH" || paymentMethod === "RAZORPAY") {
  const amountNum = parseFloat(paymentAmount);

  if (isNaN(amountNum) || amountNum <= 0) {
    alert("Payment amount is required for Cash or Razorpay payment");
    return false;
  }
}

  const formData = new FormData();
  
  // ✅ Add all form fields including password
  Object.keys(form).forEach(key => {
    if (form[key] !== undefined && form[key] !== null && form[key] !== "") {
      formData.append(key, form[key]);
    }
  });
  
  // ✅ IMPORTANT: Make sure confirmPassword is added
  // It's already in form, but let's ensure it's added
  if (form.confirmPassword) {
    formData.append("confirmPassword", form.confirmPassword);
  }

  // Append files
  if (files.aadhaarFile) {
    formData.append("aadhaarFile", files.aadhaarFile);
  }
  if (files.panFile) {
    formData.append("panFile", files.panFile);
  }
  if (files.passbookFile) {
    formData.append("passbookFile", files.passbookFile);
  }
  if (files.photoFile) {
    formData.append("photoFile", files.photoFile);
  }

  // Add payment data
 
 

const amountNum = parseFloat(paymentAmount);

if (paymentMethod === "FREE") {

  formData.append("paymentAmount", 0);

  formData.append("paymentDescription", "FREE USER");

  formData.append("paymentStatus", "FREE");

  formData.append("paymentMethod", "FREE");
}

else if (paymentMethod === "CASH") {

  formData.append("paymentAmount", amountNum);

  formData.append(
    "paymentDescription",
    paymentDescription.trim()
  );

  formData.append("paymentStatus", "PAID");

  formData.append("paymentMethod", "CASH");
}

else if (paymentMethod === "RAZORPAY") {

  formData.append("paymentAmount", amountNum);

  formData.append(
    "paymentDescription",
    paymentDescription.trim()
  );

  formData.append("paymentStatus", "PAID");

  formData.append("paymentMethod", "RAZORPAY");
}
  // ✅ Debug: Log all form data
  console.log("Sending form data:");
  for (let pair of formData.entries()) {
    console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
  }

  setLoading(true);
  try {
    const response = await api.post("/users/create-full", formData, {
      headers: { 
        "Content-Type": "multipart/form-data"
      }
    });
    
    console.log("Response:", response.data);
    
    if (response.data && response.data.success !== false) {
      alert("✅ User Created Successfully");
      
      // Reset form
      setForm({ ...initialState, employeeId: generateEmployeeId() });
      setPaymentAmount("");
      setPaymentDescription("");
      setPaymentCleared(false);
      clearFileInputs();
    } else {
      alert("❌ Error: " + (response.data?.message || "Unknown error"));
    }
    
  } catch (err) {
    console.error("Full error:", err);
    console.error("Error response:", err.response);
    console.error("Error data:", err.response?.data);
    
    // Better error message handling
    let errorMessage = "Unknown error occurred";
    if (err.response?.data) {
      if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      } else if (err.response.data.error) {
        errorMessage = err.response.data.error;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      }
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    alert("❌ Error: " + errorMessage);
  } finally {
    setLoading(false);
  }
};
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const handlePayAndCreate = async () => {
  if (!validateForm()) return;

  const amountNum = parseFloat(paymentAmount);

  if (isNaN(amountNum) || amountNum <= 0) {
    alert("Please enter payment amount first");
    return;
  }

  const scriptLoaded = await loadRazorpayScript();
  if (!scriptLoaded) {
    alert("Razorpay SDK failed to load");
    return;
  }

  setLoading(true);

  try {
    // 1. Create temporary payment/order from backend
 const orderRes = await api.post("/payments/registration/init", {
  amount: amountNum,
  description: paymentDescription || "User registration fee",
  paymentMethod: "RAZORPAY"
});

const paymentId = orderRes.data.paymentId;

    const options = {
      key: "rzp_live_T2zVuDM09kAR4w",
      amount: orderRes.data.amount * 100,
      
      currency: orderRes.data.currency,
      name: "RHCWF",
      description: paymentDescription || "User Registration",
      order_id: orderRes.data.orderId,

      handler: async function (response) {
        try {
          // 3. Verify payment
          await api.post("/payments/razorpay-verify", {
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature
          });

          // 4. Now create user
          await save();

          alert("✅ Payment successful and user created");
        } catch (err) {
          console.error(err);
          alert("Payment done but verification/user creation failed");
        }
      },

      prefill: {
        name: form.name,
        email: form.email,
        contact: form.phone
      },

      theme: {
        color: "#4361ee"
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

  } catch (err) {
    console.error(err);
    alert("Unable to start Razorpay payment");
  } finally {
    setLoading(false);
  }
};
const handleFinalCreate = async () => {

  if (paymentMethod === "RAZORPAY") {

    await handlePayAndCreate();

  } else {

    await save();
  }
};
  // Get password strength color
  const getStrengthColor = () => {
    if (passwordStrength <= 2) return "#ef4444";
    if (passwordStrength <= 4) return "#f59e0b";
    return "#10b981";
  };

  const getStrengthText = () => {
    if (passwordStrength <= 2) return "Weak";
    if (passwordStrength <= 4) return "Medium";
    return "Strong";
  };

  return (
    <div className="create-user-app">
      <Sidebar />
      <div className="create-user-main">
        <div className="page-header">
          <div>
            <h1>Create New User</h1>
            <p>Fill in the details below to add a new user to the system</p>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={handleReset} disabled={loading}>
              Reset Form
            </button>
          </div>
        </div>

        <div className="form-container">
          {/* BASIC INFORMATION */}
          <div className="card">
            <div className="card-header">
              <span className="card-icon">👤</span>
              <h3>Basic Information</h3>
              <span className="required-badge">Required</span>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input 
                  name="name" 
                  
                  value={form.name} 
                  onChange={handleChange} 
                  placeholder="Enter full name" 
                  autoComplete="off" 
                />
              </div>
              
              <div className="form-group">
                <label>Email Address <span className="required">*</span></label>
                <input 
                  type="email" 
                  name="email" 
                  value={form.email} 
                  onChange={handleChange} 
                  placeholder="user@example.com" 
                  autoComplete="off" 
                />
              </div>
              
              {/* Password Field with Strength Meter */}
              <div className="form-group">
                <label>Password <span className="required">*</span></label>
                <div className="password-wrapper">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    name="password" 
                    value={form.password} 
                    onChange={handlePasswordChange} 
                    placeholder="Create a secure password" 
                    autoComplete="off" 
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
                
                {form.password && (
                  <>
                    <div className="password-strength">
                      <div className="strength-bar">
                        <div 
                          className="strength-fill" 
                          style={{ 
                            width: `${(passwordStrength / 6) * 100}%`, 
                            backgroundColor: getStrengthColor() 
                          }}
                        />
                      </div>
                      <span className="strength-text" style={{ color: getStrengthColor() }}>
                        {getStrengthText()} Password
                      </span>
                    </div>
                    
                    {passwordErrors.length > 0 && (
                      <div className="password-errors">
                        <small>Missing:</small>
                        <ul>
                          {passwordErrors.map((err, idx) => (
                            <li key={idx}>✗ {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
                
                <small className="password-hint">
                  Password must contain: 8+ chars, 1 digit, 1 lowercase, 1 uppercase, 1 special char (@#$%^&+=!)
                </small>
              </div>
              
              {/* Confirm Password Field */}
              <div className="form-group">
                <label>Confirm Password <span className="required">*</span></label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter password"
                    autoComplete="off"
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
                
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <span className="error-text">❌ Passwords do not match</span>
                )}
                
                {form.confirmPassword && form.password === form.confirmPassword && form.password && (
                  <span className="success-text">✓ Passwords match</span>
                )}
              </div>
              
            </div>
          </div>

          {/* HIERARCHY MAPPING */}
          <div className="card">
            <div className="card-header">
              <span className="card-icon">🏢</span>
              <h3>Hierarchy Mapping</h3>
            </div>
            <div className="card-body">
              
              <div className="form-group">
                <label>User Role <span className="required">*</span></label>
                <select name="role" value={form.role} onChange={handleChange}>
                  <option value="">Select Role</option>
                  <option value="STATE_COORDINATOR">🏛️ State Coordinator</option>
                  <option value="DISTRICT_COORDINATOR">📊 District Coordinator</option>
                  <option value="BLOCK_COORDINATOR">📍 Block Coordinator</option>
                  <option value="GP_COORDINATOR">🏘️ GP Coordinator</option>
                </select>
              </div>
              <div className="form-group">
                <label>State</label>
                <select value={form.stateId} onChange={handleStateChange}>
                  <option value="">Select State</option>
                  {states.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              
              {form.role !== "STATE_COORDINATOR" && (
                <div className="form-group">
                  <label>District</label>
                  <select value={form.districtId} onChange={handleDistrictChange} disabled={!form.stateId}>
                    <option value="">Select District</option>
                    {districts.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {(form.role === "BLOCK_COORDINATOR" || form.role === "GP_COORDINATOR") && (
                <div className="form-group">
                  <label>Block</label>
                  <select value={form.blockId} onChange={handleBlockChange} disabled={!form.districtId}>
                    <option value="">Select Block</option>
                    {blocks.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {form.role === "GP_COORDINATOR" && (
                <div className="form-group">
                  <label>Gram Panchayat</label>
                  <select value={form.gpId} onChange={(e) => setForm({ ...form, gpId: e.target.value })} disabled={!form.blockId}>
                    <option value="">Select GP</option>
                    {gps.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* PERSONAL INFORMATION */}
          <div className="card">
            <div className="card-header">
              <span className="card-icon">📋</span>
              <h3>Personal Information</h3>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={form.phone} 
                    onChange={handleChange} 
                    autoComplete="off" 
                    placeholder="Mobile number" 
                  />
                </div>
                <div className="form-group">
                  <label>Employee ID</label>
                  <input 
                    name="employeeId" 
                    value={form.employeeId} 
                    disabled 
                    style={{ backgroundColor: "#f1f5f9", cursor: "not-allowed" }} 
                    autoComplete="off" 
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Access Level</label>
                <input 
                  name="level" 
                  value={form.level} 
                  onChange={handleChange} 
                  placeholder="e.g., Senior, Junior" 
                  autoComplete="off" 
                />
              </div>
            </div>
          </div>

          {/* ADDRESS DETAILS */}
          <div className="card">
            <div className="card-header">
              <span className="card-icon">📍</span>
              <h3>Address Details</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label>Village/Town</label>
                <input 
                  name="village" 
                  value={form.village} 
                  onChange={handleChange} 
                  placeholder="Village or town name" 
                  autoComplete="off" 
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Post Office</label>
                  <input 
                    name="postOffice" 
                    value={form.postOffice} 
                    onChange={handleChange} 
                    placeholder="Post office" 
                    autoComplete="off" 
                  />
                </div>
                <div className="form-group">
                  <label>PIN Code</label>
                  <input 
                    name="pin" 
                    value={form.pin} 
                    onChange={handleChange} 
                    placeholder="PIN code" 
                    autoComplete="off" 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Aadhaar Number</label>
                  <input 
                    name="aadhaar" 
                    value={form.aadhaar} 
                    onChange={handleChange} 
                    placeholder="12-digit Aadhaar" 
                    autoComplete="off" 
                  />
                </div>
                <div className="form-group">
                  <label>PAN Number</label>
                 <input
  name="pan"
  value={form.pan}
  onChange={(e) =>
    setForm({ ...form, pan: e.target.value.toUpperCase() })
  }
  placeholder="PAN card number"
  autoComplete="off"
/>
                </div>
              </div>
            </div>
          </div>

          {/* BANK DETAILS */}
          <div className="card full-width">
            <div className="card-header">
              <span className="card-icon">🏦</span>
              <h3>Bank Details</h3>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input 
                    name="bankName" 
                    value={form.bankName} 
                    onChange={handleChange} 
                    placeholder="Name of bank" 
                    autoComplete="off" 
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input 
                    name="accountNumber" 
                    value={form.accountNumber} 
                    onChange={handleChange} 
                    placeholder="Bank account number" 
                    autoComplete="off" 
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
  name="ifsc"
  value={form.ifsc}
  onChange={(e) =>
    setForm({ ...form, ifsc: e.target.value.toUpperCase() })
  }
  placeholder="IFSC code"
  autoComplete="off"
/>
                </div>
                <div className="form-group">
                  <label>Branch Name</label>
                  <input 
                    name="branch" 
                    value={form.branch} 
                    onChange={handleChange} 
                    placeholder="Branch name" 
                    autoComplete="off" 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* PAYMENT ASSIGNMENT */}
          <div className="card full-width">
            <div className="card-header">
              <span className="card-icon">💰</span>
              <h3>Payment Assignment</h3>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Amount (₹)</label>
                 <input
  type="number"
  value={paymentMethod === "FREE" ? 0 : paymentAmount}
  onChange={(e) => setPaymentAmount(e.target.value)}
  disabled={paymentMethod === "FREE"}
  placeholder={
    paymentMethod === "FREE"
      ? "Free User"
      : "Enter Payment Amount"
  }
/>
                  {/* <small>Leave 0 or empty if the user should be free</small> */}
                </div>
                <div className="form-group">
                  <label>Description (optional)</label>
                  <input
                    type="text"
                    value={paymentDescription}
                    onChange={(e) => setPaymentDescription(e.target.value)}
                    placeholder="e.g., Registration fee, Membership"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="form-group">
  <label>Payment Method</label>
  <select
  value={paymentMethod}
  onChange={(e) => setPaymentMethod(e.target.value)}
>
  <option value="CASH">Cash</option>
  <option value="FREE">Free</option>
  <option value="RAZORPAY">Razorpay Online</option>
</select>
</div>
              <div className="form-group" style={{ marginTop: "10px" }}>
                {/* <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={paymentCleared}
                    onChange={(e) => setPaymentCleared(e.target.checked)}
                    disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
                  />
                  Mark as paid immediately (cleared)
                </label> */}
                {(!paymentAmount || parseFloat(paymentAmount) <= 0) && (
                  <small style={{ color: "#888" }}></small>
                )}
              </div>
            </div>
          </div>

          {/* UPLOAD NEW DOCUMENTS with Previews */}
          <div className="card full-width">
            <div className="card-header">
              <span className="card-icon">📂</span>
              <h3>Upload New Documents</h3>
            </div>
            <div className="card-body">
              <div className="upload-docs-horizontal">
                {/* Aadhaar */}
                <div className="upload-doc-item">
                  <label>Aadhaar Image</label>
                  <input
                    ref={aadhaarRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange("aadhaar", e.target.files[0])}
                  />
                  {previews.aadhaar && (
                    <div className="image-preview">
                      <img src={previews.aadhaar} alt="Aadhaar preview" width="60" height="60" style={{ borderRadius: "8px", marginTop: "8px" }} />
                    </div>
                  )}
                </div>
                
                {/* PAN */}
                <div className="upload-doc-item">
                  <label>PAN Image</label>
                  <input
                    ref={panRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange("pan", e.target.files[0])}
                  />
                  {previews.pan && (
                    <div className="image-preview">
                      <img src={previews.pan} alt="PAN preview" width="60" height="60" style={{ borderRadius: "8px", marginTop: "8px" }} />
                    </div>
                  )}
                </div>
                
                {/* Passbook */}
                <div className="upload-doc-item">
                  <label>Passbook Image</label>
                  <input
                    ref={passbookRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange("passbook", e.target.files[0])}
                  />
                  {previews.passbook && (
                    <div className="image-preview">
                      <img src={previews.passbook} alt="Passbook preview" width="60" height="60" style={{ borderRadius: "8px", marginTop: "8px" }} />
                    </div>
                  )}
                </div>
                
                {/* Photo */}
                <div className="upload-doc-item">
                  <label>Photo</label>
                  <input
                    ref={photoRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange("photo", e.target.files[0])}
                  />
                  {previews.photo && (
                    <div className="image-preview">
                      <img src={previews.photo} alt="Photo preview" width="60" height="60" style={{ borderRadius: "50%", marginTop: "8px" }} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* FORM ACTIONS */}
          <div className="form-actions">
            <button className="btn-cancel" onClick={handleReset} disabled={loading}>
              Cancel
            </button>
       <button
  className="btn-submit"
  onClick={handleFinalCreate}
  disabled={loading}
>
  {loading
    ? "Processing..."
    : paymentMethod === "RAZORPAY"
      ? "💳 Razorpay Pay & Create User"
      : paymentMethod === "FREE"
        ? "🆓 Create Free User"
        : "💵 Cash Pay & Create User"}
</button>
          </div>
        </div>
      </div>
    </div>
  );
}